import React, { useCallback, useEffect, useState } from "react";
import Shell from "@/components/Shell";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Plus, Send, Bot, Pencil, Trash2, Sparkles, Reply, Eye, Users, CheckCircle2, Pause, BookOpen, Zap, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import SequenceBuilder from "@/components/outreach/SequenceBuilder";
import EnrollDialog from "@/components/outreach/EnrollDialog";
import EnrollmentsTable from "@/components/outreach/EnrollmentsTable";
import EngineBar from "@/components/outreach/EngineBar";
import PlaybookGallery from "@/components/outreach/PlaybookGallery";
import AutomationRules from "@/components/outreach/AutomationRules";
import ApprovalQueue from "@/components/outreach/ApprovalQueue";
import EmptyState from "@/components/EmptyState";
import AgentWizard from "@/components/outreach/AgentWizard";
import { fmtDelay } from "@/components/outreach/shared";

export default function Agents() {
  const [sequences, setSequences] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [engine, setEngine] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [enrollments, setEnrollments] = useState([]);
  const [approvals, setApprovals] = useState([]);
  const [builder, setBuilder] = useState({ open: false, seq: null });
  const [enrollOpen, setEnrollOpen] = useState(false);
  const [playbooksOpen, setPlaybooksOpen] = useState(false);
  const [rulesOpen, setRulesOpen] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);

  const load = useCallback(async () => {
    const [s, a, c, e] = await Promise.all([api.get("/sequences"), api.get("/email-accounts"), api.get("/contacts"), api.get("/engine/status")]);
    setSequences(s.data); setAccounts(a.data); setContacts(c.data); setEngine(e.data);
    setSelectedId((id) => {
      const wanted = new URLSearchParams(window.location.search).get("sequence");
      if (wanted && s.data.some((x) => x.id === wanted)) { window.history.replaceState({}, "", "/agents"); return wanted; }
      return id && s.data.some((x) => x.id === id) ? id : s.data[0]?.id || null;
    });
  }, []);
  const loadEnrollments = useCallback(async () => {
    if (!selectedId) { setEnrollments([]); setApprovals([]); return; }
    const [{ data }, ap] = await Promise.all([api.get("/enrollments", { params: { sequence_id: selectedId } }), api.get("/approvals", { params: { sequence_id: selectedId } })]);
    setEnrollments(data); setApprovals(ap.data);
  }, [selectedId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadEnrollments(); }, [loadEnrollments]);
  useEffect(() => { const t = setInterval(() => { load(); loadEnrollments(); }, 30000); return () => clearInterval(t); }, [load, loadEnrollments]);

  const selected = sequences.find((s) => s.id === selectedId);
  const refresh = () => { load(); loadEnrollments(); };

  const remove = async (seq) => {
    if (!window.confirm(`Delete "${seq.name}"? Active enrollments will be stopped.`)) return;
    try { await api.delete(`/sequences/${seq.id}`); toast.success("Sequence deleted"); refresh(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };

  return (
    <Shell title="Agents & Sequences" actions={
      <>
        <Button size="sm" variant="outline" data-testid="btn-automation-rules" onClick={() => setRulesOpen(true)}><Zap className="w-4 h-4 mr-2" />Automation</Button>
        <Button size="sm" variant="outline" data-testid="btn-playbooks" onClick={() => setPlaybooksOpen(true)}><BookOpen className="w-4 h-4 mr-2" />Playbooks</Button>
        <Button size="sm" variant="outline" data-testid="btn-new-sequence" onClick={() => setBuilder({ open: true, seq: null })}><Plus className="w-4 h-4 mr-2" />New sequence</Button>
        <Button size="sm" data-testid="btn-new-agent" onClick={() => setWizardOpen(true)}><Sparkles className="w-4 h-4 mr-2" />New AI agent</Button>
      </>
    }>
      <div className="flex flex-col h-full">
        <EngineBar engine={engine} onTick={refresh} />
        <div className="flex flex-1 min-h-0">
          <aside className="w-72 border-r border-border overflow-y-auto shrink-0 bg-[hsl(var(--sidebar))] stagger" data-testid="sequence-list">
            {sequences.map((s) => (
              <button key={s.id} onClick={() => setSelectedId(s.id)} data-testid={`sequence-${s.id}`}
                className={`relative w-full text-left px-4 py-3 border-b border-border transition-[background-color] duration-150 ${selectedId === s.id ? "bg-card shadow-[inset_3px_0_0_hsl(var(--primary))]" : "hover:bg-card/60"}`}>
                <div className="flex items-center gap-2">
                  <Bot className={`w-4 h-4 shrink-0 ${s.active ? "text-primary" : "text-muted-foreground"}`} />
                  <div className="text-sm font-medium truncate flex-1">{s.name}</div>
                  {!s.active && <span className="text-[10px] uppercase tracking-[0.08em] text-muted-foreground">off</span>}
                </div>
                <div className="mt-1 flex items-center gap-3 text-[11px] mono text-muted-foreground">
                  <span>{s.steps.length} step{s.steps.length === 1 ? "" : "s"}</span>
                  <span className="text-emerald-500">{s.stats.active} active</span>
                  <span className="text-blue-500">{s.stats.replied} replied</span>
                  {s.stats.awaiting > 0 && <span className="text-amber-500" data-testid={`seq-awaiting-${s.id}`}>{s.stats.awaiting} to approve</span>}
                </div>
              </button>
            ))}
            {sequences.length === 0 && <div className="p-4"><EmptyState icon={Bot} title="No agents yet" hint="Create an AI agent from your website, install a playbook, or build a sequence by hand." action={<Button size="sm" onClick={() => setWizardOpen(true)} data-testid="empty-new-agent"><Sparkles className="w-3.5 h-3.5 mr-1.5" />Create AI agent</Button>} /></div>}
          </aside>

          <section className="flex-1 overflow-y-auto">
            {selected ? (
              <div className="p-6 space-y-6" data-testid="sequence-detail">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <h2 className="text-xl font-semibold tracking-tight truncate" data-testid="sequence-detail-name">{selected.name}</h2>
                    <div className="text-xs text-muted-foreground mt-1 flex flex-wrap gap-3">
                      <span>{selected.stop_on_reply ?? true ? "Stops on reply" : "Continues after reply"}</span>
                      <span>·</span>
                      <span>{selected.track_opens ?? true ? "Open/click tracking on" : "No tracking"}</span>
                      {selected.require_approval && <><span>·</span><span className="text-amber-500 inline-flex items-center gap-1" data-testid="seq-approval-badge"><ShieldCheck className="w-3 h-3" />Human approval before each send</span></>}
                      {(selected.ai_reply_mode || "draft") !== "off" && <><span>·</span><span className="text-primary inline-flex items-center gap-1" data-testid="seq-reply-mode-badge"><Sparkles className="w-3 h-3" />{selected.ai_reply_mode === "auto" ? "AI replies autonomously" : "AI drafts replies"}</span></>}
                      {selected.playbook_key && <><span>·</span><span className="inline-flex items-center gap-1"><BookOpen className="w-3 h-3" />Playbook</span></>}
                      {selected.offer && <><span>·</span><span className="truncate max-w-xs">Offer: {selected.offer}</span></>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Button variant="outline" size="sm" data-testid="btn-edit-sequence" onClick={() => setBuilder({ open: true, seq: selected })}><Pencil className="w-3.5 h-3.5 mr-1.5" />Edit</Button>
                    <Button variant="outline" size="sm" data-testid="btn-delete-sequence" onClick={() => remove(selected)}><Trash2 className="w-3.5 h-3.5 mr-1.5" />Delete</Button>
                    <Button size="sm" data-testid={`btn-enroll-${selected.id}`} onClick={() => setEnrollOpen(true)}><Send className="w-4 h-4 mr-1.5" />Enroll contacts</Button>
                  </div>
                </div>

                <div className="grid grid-cols-3 lg:grid-cols-6 gap-2 stagger">
                  <Kpi icon={Users} label="Enrolled" value={selected.stats.enrolled} test="kpi-enrolled" />
                  <Kpi icon={Send} label="Sent" value={selected.stats.sent} test="kpi-sent" />
                  <Kpi icon={Eye} label="Opened" value={selected.stats.opened} test="kpi-opened" />
                  <Kpi icon={Reply} label="Replied" value={selected.stats.replied} test="kpi-replied" accent="text-blue-500" />
                  <Kpi icon={CheckCircle2} label="Completed" value={selected.stats.completed} test="kpi-completed" />
                  <Kpi icon={Pause} label="Paused/Failed" value={selected.stats.paused + selected.stats.failed} test="kpi-paused" />
                </div>

                {approvals.length > 0 && <ApprovalQueue approvals={approvals} onChanged={refresh} />}

                <div>
                  <div className="text-[10px] uppercase tracking-[0.1em] font-medium text-muted-foreground mb-2">Steps</div>
                  <div className="border border-border rounded-md divide-y divide-border">
                    {selected.steps.map((step, i) => (
                      <div key={i} className="flex gap-4 px-4 py-3" data-testid={`step-view-${i}`}>
                        <div className="w-24 shrink-0">
                          <div className="text-[10px] uppercase tracking-[0.08em] text-muted-foreground">Step {i + 1}</div>
                          <div className="mono text-xs">{fmtDelay(step.delay_days)}</div>
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium truncate flex items-center gap-2">{step.subject}{step.ai_personalize && <span className="inline-flex items-center gap-1 text-[10px] text-primary border border-primary/30 bg-primary/10 px-1.5 rounded"><Sparkles className="w-3 h-3" />AI</span>}</div>
                          <div className="text-xs text-muted-foreground line-clamp-2 whitespace-pre-wrap mt-0.5">{step.body}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <div className="text-[10px] uppercase tracking-[0.1em] font-medium text-muted-foreground mb-2">Enrollments</div>
                  <EnrollmentsTable enrollments={enrollments} totalSteps={selected.steps.length} onChanged={refresh} />
                </div>
              </div>
            ) : (
              <div className="p-10 text-sm text-muted-foreground">Create a sequence to start outbound.</div>
            )}
          </section>
        </div>
      </div>

      <SequenceBuilder open={builder.open} seq={builder.seq} contacts={contacts} onOpenChange={(o) => setBuilder((b) => ({ ...b, open: o }))} onSaved={(s) => { setBuilder({ open: false, seq: null }); setSelectedId(s.id); refresh(); }} />
      <EnrollDialog open={enrollOpen} onOpenChange={setEnrollOpen} seq={selected} contacts={contacts} accounts={accounts} onDone={() => { setEnrollOpen(false); refresh(); }} />
      <PlaybookGallery open={playbooksOpen} onOpenChange={setPlaybooksOpen} onInstalled={(s) => { setPlaybooksOpen(false); setSelectedId(s.id); refresh(); }} />
      <AutomationRules open={rulesOpen} onOpenChange={setRulesOpen} />
      <AgentWizard open={wizardOpen} onOpenChange={setWizardOpen} onCreated={(s) => { setSelectedId(s.id); refresh(); }} />
    </Shell>
  );
}

function Kpi({ icon: Icon, label, value, test, accent }) {
  return (
    <div className="relative border border-border rounded-lg bg-card px-3 py-2.5 lift" data-testid={test}>
      <Icon className="absolute right-2.5 top-2.5 w-3.5 h-3.5 text-muted-foreground/50" />
      <div className="eyebrow">{label}</div>
      <div className={`display text-xl font-extrabold tracking-tight mono mt-1 ${accent || ""}`}>{value}</div>
    </div>
  );
}
